import React, { useState, useEffect, useMemo } from 'react'
import { Text, Heading, Input, Select, SortableTableHeader, LoadingSpinner, Button } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase } from '../lib/supabase'
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  XMarkIcon,
  ServerIcon,
  ChatBubbleLeftRightIcon,
  XCircleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'

interface MessageData {
  id: number
  message: string
  type: string
  to: string
  created_at: string
}

const MensagensAtivas = () => {
  const [messagesData, setMessagesData] = useState<MessageData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroDestinatario, setFiltroDestinatario] = useState('')
  const [filtroMensagem, setFiltroMensagem] = useState('')
  const [tiposDisponiveis, setTiposDisponiveis] = useState<string[]>([])
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<MessageData | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  
  // Estados de ordenação
  const [sortBy, setSortBy] = useState<'type' | 'to' | 'message' | 'created_at'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true)
        
        // Buscar todos os registros usando paginação
        const allData: MessageData[] = []
        const batchSize = 1000
        let from = 0
        let hasMore = true

        while (hasMore) {
          const { data, error } = await supabase
            .from('messages_sent')
            .select('*')
            .order('created_at', { ascending: false })
            .range(from, from + batchSize - 1)

          if (error) {
            throw error
          }

          if (data && data.length > 0) {
            allData.push(...(data as MessageData[]))
            
            // Se retornou menos que o batchSize, não há mais dados
            if (data.length < batchSize) {
              hasMore = false
            } else {
              from += batchSize
            }
          } else {
            hasMore = false
          }
        }

        setMessagesData(allData)
        
        // Extrair tipos únicos dos dados
        const tipos = allData.map(item => item.type).filter(tipo => tipo)
        const tiposUnicos = Array.from(new Set(tipos)).sort()
        setTiposDisponiveis(tiposUnicos)
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchMessages()
  }, [])

  const messagesFiltrados = useMemo(() => {
    const filtered = messagesData.filter(item => {
      const matchTipo = !filtroTipo || item.type === filtroTipo
      const matchDestinatario = !filtroDestinatario || item.to.toLowerCase().includes(filtroDestinatario.toLowerCase())
      const matchMensagem = !filtroMensagem || item.message.toLowerCase().includes(filtroMensagem.toLowerCase())
      
      return matchTipo && matchDestinatario && matchMensagem
    })

    // Aplicar ordenação
    return filtered.sort((a, b) => {
      let aValue: any = a[sortBy]
      let bValue: any = b[sortBy]

      // Tratamento especial para campos específicos
      if (sortBy === 'created_at') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      } else {
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [messagesData, filtroTipo, filtroDestinatario, filtroMensagem, sortBy, sortOrder])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [filtroTipo, filtroDestinatario, filtroMensagem])

  // Calcular dados da paginação
  const totalPages = Math.ceil(messagesFiltrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const messagesPaginados = messagesFiltrados.slice(startIndex, endIndex)

  const handleRowClick = (item: MessageData) => {
    setSelectedMessage(item)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedMessage(null)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as any)
      setSortOrder('asc')
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const getTypeIcon = (type: string) => {
    return <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-500" />
  }

  const getTypeText = (type: string) => {
    return type || 'Não definido'
  }

  const getTypeColor = (type: string) => {
    return 'text-blue-600 dark:text-blue-400'
  }

  const getDestinatarioIcon = (to: string) => {
    return <EnvelopeIcon className="h-5 w-5 text-green-500" />
  }

  const getDestinatarioText = (to: string) => {
    if (!to) return 'Não definido'
    
    // Remove espaços e caracteres especiais para verificar se é telefone
    const cleanTo = to.replace(/\s/g, '')
    
    // Verifica se é um número de telefone (contém apenas dígitos, +, -, (, ))
    const isPhoneNumber = /^[\d\+\-\(\)]+$/.test(cleanTo) && /\d/.test(cleanTo)
    
    if (isPhoneNumber) {
      return formatPhoneNumber(to)
    }
    
    return to
  }

  const getDestinatarioColor = (to: string) => {
    return 'text-green-600 dark:text-green-400'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return 'Sem mensagem'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return 'Não definido'
    
    // Remove todos os caracteres não numéricos
    const numbers = phone.replace(/\D/g, '')
    
    // Se tem 11 dígitos (celular com DDD)
    if (numbers.length === 11) {
      return `+55 (${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7)}`
    }
    
    // Se tem 10 dígitos (telefone fixo com DDD)
    if (numbers.length === 10) {
      return `+55 (${numbers.slice(0, 2)})${numbers.slice(2, 6)}-${numbers.slice(6)}`
    }
    
    // Se tem 9 dígitos (celular sem DDD)
    if (numbers.length === 9) {
      return `+55 (31)${numbers.slice(0, 5)}-${numbers.slice(5)}`
    }
    
    // Se tem 8 dígitos (telefone fixo sem DDD)
    if (numbers.length === 8) {
      return `+55 (31)${numbers.slice(0, 4)}-${numbers.slice(4)}`
    }
    
    // Se tem 13 dígitos (com código do país 55)
    if (numbers.length === 13 && numbers.startsWith('55')) {
      const ddd = numbers.slice(2, 4)
      const number = numbers.slice(4)
      if (number.length === 9) {
        return `+55 (${ddd})${number.slice(0, 5)}-${number.slice(5)}`
      } else if (number.length === 8) {
        return `+55 (${ddd})${number.slice(0, 4)}-${number.slice(4)}`
      }
    }
    
    // Para outros formatos, retorna o número original
    return phone
  }

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" className="text-gray-600 dark:text-gray-400">
              Carregando mensagens...
            </Text>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" className="text-red-600 dark:text-red-400">
              Erro ao carregar mensagens: {error}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="atendimentos_mensagens_ativas" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Mensagens Ativas
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                Histórico de mensagens enviadas pelo sistema via WhatsApp
              </Text>
            </div>

            {/* Topo Colorido - Valores */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {messagesFiltrados.length}
                    </Text>
                    <Text size="sm" className="text-white">
                      Total de Mensagens
                    </Text>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros (padrão SilvIA) */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Filtros
                  </Heading>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                  <div className="md:col-span-3">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Tipo
                    </Text>
                    <Select
                      value={filtroTipo}
                      onChange={(e) => setFiltroTipo(e.target.value)}
                      className="w-full"
                    >
                      <option value="">Todos os tipos</option>
                      {tiposDisponiveis.map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Destinatário
                    </Text>
                    <Input
                      type="text"
                      placeholder="Digite o destinatário..."
                      value={filtroDestinatario}
                      onChange={(e) => setFiltroDestinatario(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Mensagem
                    </Text>
                    <Input
                      type="text"
                      placeholder="Digite parte da mensagem..."
                      value={filtroMensagem}
                      onChange={(e) => setFiltroMensagem(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-2 flex justify-start">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFiltroTipo('')
                        setFiltroDestinatario('')
                        setFiltroMensagem('')
                      }}
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Histórico de Mensagens
                  </Heading>
                </div>
                
                <div className="flex-1 overflow-auto">
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                      <tr>
                        <SortableTableHeader
                          sortKey="type"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/5"
                        >
                          Tipo
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="to"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/5"
                        >
                          Destinatário
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="message"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-2/5"
                        >
                          Mensagem
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="created_at"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/5 text-center"
                        >
                          Data
                        </SortableTableHeader>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {messagesPaginados.map((item) => (
                        <tr 
                          key={item.id} 
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => handleRowClick(item)}
                        >
                          <td className="px-4 py-4 text-left">
                            <Text size="sm" className={`font-medium ${getTypeColor(item.type)}`}>
                              {getTypeText(item.type)}
                            </Text>
                          </td>
                          <td className="px-4 py-4 text-left">
                            <div className="flex items-center gap-2">
                              {getDestinatarioIcon(item.to)}
                              <Text size="sm" className={`font-medium ${getDestinatarioColor(item.to)}`}>
                                {getDestinatarioText(item.to)}
                              </Text>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-left">
                            <Text size="sm" className="text-gray-700 dark:text-gray-300">
                              {truncateText(item.message)}
                            </Text>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <Text size="sm" className="text-gray-500 dark:text-gray-400">
                              {formatDate(item.created_at)}
                            </Text>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Controles de Paginação */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, messagesFiltrados.length)} de {messagesFiltrados.length} registros
                        </Text>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number;
                            if (totalPages <= 5) {
                              pageNumber = i + 1;
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i;
                            } else {
                              pageNumber = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </PermissionGuard>
      </div>

      {/* Modal de Detalhes */}
      {isDetailsModalOpen && selectedMessage && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDetailsModal}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Detalhes da Mensagem Ativa
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    ID: {selectedMessage.id} - Tipo: {selectedMessage.type}
                  </Text>
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tabela de Configuração */}
              <div className="flex-1 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Campo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedMessage.id}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Tipo
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(selectedMessage.type)}
                          <Text size="sm" className={`font-medium ${getTypeColor(selectedMessage.type)}`}>
                            {getTypeText(selectedMessage.type)}
                          </Text>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Destinatário
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-center gap-2">
                          {getDestinatarioIcon(selectedMessage.to)}
                          <Text size="sm" className={`font-medium ${getDestinatarioColor(selectedMessage.to)}`}>
                            {getDestinatarioText(selectedMessage.to)}
                          </Text>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Mensagem
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="max-w-4xl">
                          <div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                  <Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">
                                    Conteúdo da Mensagem
                                  </Text>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                  TEXTO
                                </div>
                              </div>
                            </div>
                            <div className="p-6">
                              <div className="text-sm text-gray-900 dark:text-gray-100 font-mono overflow-auto max-h-96 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border">
                                {selectedMessage.message || 'Sem mensagem'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data de Criação
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedMessage.created_at)}
                        </Text>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MensagensAtivas
